import React, { useState, useEffect, useRef, useContext } from 'react';
import ReactToPrint from 'react-to-print';
import {
    Button,
    Label,
    Radio,
    Select,
    TextInput,
    Modal,
    Table,
    Badge,
} from 'flowbite-react';
import { toast } from 'react-toastify';
import { ImCross } from "react-icons/im";
import { createDateWithCurrentTime, formatCurrency, formatDate, formatTime, roundValue } from '../utils/utils';
import axios from 'axios';
import { UserContext } from '../context';

const PaymentInfo = ({ showPayment, setPaymentMethod, setPaymentAmount }) => {
    if (showPayment) {
        return (
            <div className="grid grid-cols-3">
                <div className="col-span-2 flex items-center">
                    <div className="p-2 w-1/4">
                        <Label htmlFor="payment" value="Mode of payment" />
                    </div>
                    <div className='w-1/2'>
                        <Select id="payment" sizing='lg' defaultValue={'DEFAULT'} onChange={(event) => { setPaymentMethod(event.target.options[event.target.selectedIndex].innerText); }} required>
                            <option className='text-xl' value="DEFAULT" disabled>Select</option>
                            <option className='text-xl'>Cash</option>
                            <option className='text-xl'>Credit Card</option>
                            <option className='text-xl'>Bank Transfer</option>
                            <option className='text-xl'>Others</option>
                        </Select>
                    </div>
                </div>
                {/* <div className="flex items-center">
                    <div className="p-2">
                        <Label htmlFor="payment-value" value="Value" />
                    </div>
                    <div className='p-2 w-1/2'>
                        <input type="number" name="payment-value" onChange={(event) => setPaymentAmount(event.target.value)} className='bg-gradient-to-br from-theme-primary to-theme-secondary rounded-lg text-md text-white' />
                    </div>
                </div> */}
            </div>
        )
    }
}

const Invoice = React.forwardRef(({
    transactions,
    setTransactions,
    date,
    setDate,
    shopData,
    totalPayable,
    handleSubmit,
    handleSaveAndPrint,
    setOpenModal,
    customerData,
    userDetail,
    resetForm
}, ref) => {

    // validation for transactions
    const [printReady, setPrintReady] = useState(false);

    useEffect(() => {
        if (transactions && transactions.length > 0) {
            setPrintReady(true);
        }
    }, [transactions]);

    if (!printReady) {
        return null;
    }

    return (
        <div ref={ref} className="w-full max-w-md mx-auto bg-white p-4 shadow-sm border invoice-to-print flex flex-col">
            {/* Close button */}
            <div className="flex justify-end w-full print:hidden">
                <button onClick={() => { setOpenModal(false); resetForm(); }} className="text-gray-500 hover:text-gray-700">
                    <ImCross size={20} />
                </button>
            </div>

            {/* Header */}
            <div className="text-center mb-4">
                <h1 className="text-xl font-bold mb-1">{shopData && shopData.name}</h1>
                <p className="text-sm mb-1">ABN {shopData && shopData.abn || '14660270913'}</p>
                <p className="text-sm">{shopData && shopData.address}</p>
                {shopData.phone && <p className="text-sm mt-2">{shopData.phone || '+6834667386'}</p>}
            </div>

            {/* Customer Details */}
            {customerData && (
                <div className="mb-4">
                    <p className="font-medium">{transactions[0]?.customerName}</p>
                    <p className="text-sm">{transactions[0]?.customerPhoneNumber}</p>
                </div>
            )}

            {/* Invoice Details */}
            <div className="text-center mb-4">
                <h2 className="text-lg font-bold">Tax Invoice</h2>
            </div>

            {/* Transaction Items */}
            <div className="mb-4">
                <div className="flex justify-between mb-2 font-medium border-b pb-1">
                    <span>Item</span>
                    <span>Total</span>
                </div>

                {transactions.map((item, index) => (
                    <div key={index} className="mb-3">
                        <p className="text-sm font-medium pt-2">TransactionId: {item.transactionId}</p>
                        <div className="flex justify-between mb-1">
                            <div>
                                <p className="font-medium">{item.currency} - {item.transactionMode}</p>
                                <p className="text-sm">Qty: {parseFloat(item.amount).toLocaleString()}</p>
                                <p className="text-sm">Rate: {parseFloat(item.rate).toFixed(4)}</p>
                                <p className="text-sm">
                                    {item.commissionType === 'per'
                                        ? `Commission Fee : ${item.per}`
                                        : `Flat Fee: $${item.fixed}`}
                                </p>
                                {parseFloat(item.cashHandling) > 0 && (
                                    <p className="text-sm">Cash Handling: {formatCurrency(item.cashHandling, 'AUD')}</p>
                                )}
                            </div>
                            <p className="font-medium">{formatCurrency(item.payableAmount, 'AUD')}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Totals */}
            <div className="border-t border-b py-2 mb-4">
                <div className="flex justify-between font-bold">
                    <span>TOTAL</span>
                    <span>{formatCurrency(totalPayable, 'AUD')}</span>
                </div>
                <div className="text-sm flex justify-between">
                    <span>Includes GST of</span>
                    <span>{formatCurrency(totalPayable * 0.1, 'AUD')}</span>
                </div>
            </div>

            {/* Payment Method */}
            <div className="mb-4">
                <p className="font-medium">Paid by</p>
                {Object.entries(
                    transactions.reduce((acc, transaction) => {
                        const paymentMethod = transaction.cashParticular || 'Cash';
                        acc[paymentMethod] = (acc[paymentMethod] || 0) + parseFloat(transaction.payableAmount);
                        return acc;
                    }, {})
                ).map(([paymentMethod, total], index) => (
                    <div key={index} className="flex justify-between">
                        <span>{paymentMethod}</span>
                        <span>{formatCurrency(total, 'AUD')}</span>
                    </div>
                ))}
            </div>
            {/* Footer */}
            <div className="text-center text-sm py-6 font-bold">
                <p>We appreciate your visit to <span className='font-extrabold'>{shopData.name}!</span> </p>
                <p>Kindly note, we don’t offer refunds for change of mind. We can’t wait to welcome you back in store!</p>
            </div>

            {/* Staff & Date */}
            <div className="flex justify-between text-sm mt-2">
                <div>
                    <p>Served by</p>
                    <p>{userDetail?.name}</p>
                </div>
                <div className="text-right">
                    <p>{formatDate(date)}</p>
                    {/* <p>{formatTime(new Date())}</p> */}
                </div>
            </div>

            {/* Buttons - only visible when printing */}
            <div className="mt-6 flex justify-center space-x-4 print:hidden">
                <Button
                    onClick={() => {
                        const result = handleSubmit();
                        if (result) {
                            setTransactions([]);
                            setOpenModal(false);
                        }
                    }}
                    className="bg-red-600 text-white px-5 hover:bg-red-700"
                >
                    SAVE
                </Button>
                <Button
                    onClick={handleSaveAndPrint}
                    className="bg-red-600 text-white px-5 hover:bg-red-700"
                >
                    SAVE & PRINT
                </Button>
            </div>
        </div>
    );
});

const FormTemplate = ({
    handleSubmit,
    rates,
    selectedCurrency,
    localCurrencyAmount,
    setSelectedCurrency,
    currencyName,
    setCurrencyName,
    currencyAmount,
    setCurrencyAmount,
    convertedAmount,
    setConvertedAmount,
    commission,
    setCommission,
    commissionType,
    setCommissionType,
    commissionVal,
    setCommissionVal,
    cashHandling,
    setCashHandling,
    cashHandlingType,
    setCashHandlingType,
    cashHandlingVal,
    setCashHandlingVal,
    setPaymentAmount,
    setPaymentMethod,
    handleTransactions,
    totalConverted,
    totalReceived,
    totalCommission,
    totalAmount,
    totalPayable,
    showPayment,
    transactions,
    setTransactions,
    date,
    setDate,
    reverse,
    setReverse,
    setTotalAmount,
    setTotalConverted,
    setTotalCommission,
    setTotalCashHandling,
    setTotalPayable,
    setTotalReceived,
}) => {

    const { state } = useContext(UserContext);

    // user token for API request
    const token = JSON.parse(window.localStorage.getItem("auth"))?.token;
    // user details
    const userDetail = JSON.parse(window.localStorage.getItem("auth"))?.user;

    const [openModal, setOpenModal] = useState(false);
    const [userModal, setUserModal] = useState(false);
    const [customerData, setCustomerData] = useState({});
    const [existingCustomerData, setExistingCustomerData] = useState({});
    const [newCustomerModal, setNewCustomerModal] = useState(false);
    const [showCustomerHistoryModal, setShowCustomerHistoryModal] = useState(false);
    const [existingCustomerTransactions, setExistingCustomerTransactions] = useState([]);
    const [shouldPrint, setShouldPrint] = useState(false);
    const [shopData, setShopData] = useState({});
    const [availableBalance, setAvailableBalance] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const componentRef = useRef();
    const printButtonRef = useRef(null);


    useEffect(() => {
        const fetchShop = async () => {
            try {
                const response = await axios.get(process.env.REACT_APP_API_URL + `getShop`, {
                    params: {
                        shopId: userDetail.shopId
                    },
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                const shop = response.data;
                // rates.AUD = 1;
                setShopData(shop);

            } catch (error) {
                console.log(error);
            }
        };
        fetchShop();
    }, [])

    useEffect(() => {
        if (shouldPrint) {
            setTimeout(() => {
                if (printButtonRef.current) {
                    printButtonRef.current.click();
                }
                setShouldPrint(false);
            }, 100);  // Delay to ensure button is rendered
        }
    }, [shouldPrint]);

    const resetForm = () => {
        setSelectedCurrency(0);
        setCurrencyName('');
        setCurrencyAmount(0);
        setConvertedAmount(0);
        setCommission(0);
        setCashHandlingType('');
        setCashHandlingVal(0);
        setCashHandling(0);
        setPaymentAmount(0);
        setPaymentMethod('');
        setReverse(0);
        setTransactions([]);
        setTotalAmount(0);
        setTotalConverted(0);
        setTotalCommission(0);
        setTotalCashHandling(0);
        setTotalPayable(0);
        setTotalReceived(0);
        setDate(new Date().toISOString().slice(0, 10));
        setCustomerData({});
        setExistingCustomerData({});
    }

    const handleSaveAndPrint = async (e) => {
        if (e) e.preventDefault();
        try {
            // Wait for submit to complete
            const submitted = await handleSubmit(e);

            if (submitted) {
                // Store transactions temporarily for printing
                const currentTransactions = [...transactions];

                // Print with current data
                await new Promise((resolve) => {
                    setTimeout(() => {
                        if (printButtonRef.current && currentTransactions.length > 0) {
                            printButtonRef.current.click();
                            resolve();
                        }
                    }, 500);
                });

                // Wait for print to complete then reset states
                setTimeout(() => {
                    // Reset all form states
                    resetForm();

                    // Close modals
                    setOpenModal(false);
                    setUserModal(false);

                    // Clear customer data
                    setCustomerData({});

                    // Show success message
                    // toast.success('Transaction completed and printed successfully');
                }, 1000); // Wait for print to complete
            }
        } catch (error) {
            console.error('Print error:', error);
            toast.error('Error printing invoice');
        }
    };

    const handleCustomerInfo = async (e) => {
        e.preventDefault();

        if (customerData.customerName && customerData.phoneNumber) {
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}saveCustomerData`,
                    customerData,
                    {
                        params: {
                            shopId: userDetail?.shopId,
                        },
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (response.status === 201) {
                    setExistingCustomerData(response.data.data.user);

                    setTransactions(transactions.map(transaction => ({
                        ...transaction,
                        customerName: response.data.data.user.name,
                        customerPhoneNumber: response.data.data.user.phoneNumber
                    })));

                    // Clear form fields
                    setCustomerData({});

                    setUserModal(false);
                    setNewCustomerModal(true);

                    toast.success('Success: Customer Information Saved');
                } else if (response.status === 200) {
                    setExistingCustomerData(response.data.data.user);

                    setTransactions(transactions.map(transaction => ({
                        ...transaction,
                        customerName: response.data.data.user.name,
                        customerPhoneNumber: response.data.data.user.phoneNumber
                    })));

                    setExistingCustomerTransactions(response.data.data.data);

                    // Clear form fields
                    setCustomerData({});

                    setShowCustomerHistoryModal(true);
                    // toast.success('Success: Customer Information Found');
                } else {
                    toast.error('Error: Unable to save customer information.');
                }
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Server error occurred.';
                toast.error(errorMessage);
                console.error('Error saving customer:', error);
            }
        } else {
            toast.error('Please fill the required fields.');
        }
    };

    const currencyOptions = Object.values(rates).map((value) => (
        <option className='text-xl' key={value._id} value={showPayment ? value.sellRate : value.buyRate}>{value.currency}</option>
    ));

    const handleReverse = () => {
        const convertedAmountInAUD = commissionType === 'per' ? (showPayment ? parseFloat(reverse) / ((100 + parseFloat(commissionVal)) / 100) : parseFloat(reverse) / ((100 - parseFloat(commissionVal)) / 100)) : (showPayment ? (parseFloat(reverse) + parseFloat(commissionVal)) : (parseFloat(reverse) - parseFloat(commissionVal)));
        const convertedCurrency = showPayment ? roundValue(convertedAmountInAUD * selectedCurrency) : roundValue(convertedAmountInAUD * selectedCurrency);
        setCurrencyAmount(convertedCurrency);
    }

    const handleSave = (payment) => {
        if (transactions.length < 1) {
            const checkTransaction = handleTransactions();
            if (checkTransaction) {
                const paymentAmount = (payment && !reverse) || (!payment && reverse) ? (roundValue(convertedAmount) + roundValue(commission) + roundValue(cashHandling)).toFixed(2) : (roundValue(convertedAmount) - roundValue(commission) - roundValue(cashHandling)).toFixed(2);
                setUserModal(true);
            }
        } else {
            setUserModal(true);
        }
    }
    useEffect(() => {
        const convertedAmount = () => {
            (selectedCurrency !== 0) && setConvertedAmount(currencyAmount * (1 / selectedCurrency));
        };
        convertedAmount();
    }, [currencyAmount, selectedCurrency, setConvertedAmount])

    useEffect(() => {
        const setCommissions = () => {
            // console.log('rates: ', rates);
            setCommission(commissionVal ? (commissionType === 'per' ? (parseFloat(convertedAmount) * (parseFloat(commissionVal) / 100)) : commissionVal) : 0);
            setCashHandling(cashHandlingVal ? (cashHandlingType === 'per' ? (parseFloat(convertedAmount) * (parseFloat(cashHandlingVal) / 100)) : cashHandlingVal) : 0);
        };
        setCommissions();
    }, [commissionType, commissionVal, cashHandlingType, cashHandlingVal, convertedAmount, setCommission, setCashHandling])

    return (
        <>
            <form className="flex flex-col gap-4">
                <div className="grid grid-cols-3">
                    <div className="col-span-2 flex items-center">
                        <div className="p-2 w-1/4">
                            <Label htmlFor="currency" value="Select Currency" />
                        </div>
                        <div className='w-1/2'>
                            <Select
                                id="currency"
                                sizing='lg'
                                defaultValue={'DEFAULT'}
                                onChange={(event) => {
                                    setSelectedCurrency(event.target.value);
                                    const selectedCurrencyName = event.target.options[event.target.selectedIndex].innerText;
                                    setCurrencyName(selectedCurrencyName);

                                    // Find the selected currency in rates and set its balance
                                    const selectedCurrency = Object.values(rates).find(rate => rate.currency === selectedCurrencyName);
                                    setAvailableBalance(selectedCurrency?.totalAmount || 0);
                                }}
                                required
                            >
                                <option className='text-xl' value="DEFAULT" disabled>Select</option>
                                {currencyOptions}
                            </Select>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <div className="p-2">
                            <Label htmlFor="rate" value="Rate" />
                        </div>
                        <div className='p-2 w-1/2'>
                            {/* <div id="rate" className='bg-gradient-to-br from-theme-primary to-theme-secondary rounded-lg text-xl text-white p-3 w-24 text-center'>{parseFloat(selectedCurrency).toFixed(4)}</div> */}
                            <input
                                id="rate"
                                type="number"
                                value={selectedCurrency}
                                onChange={(e) => setSelectedCurrency(e.target.value)}
                                onBlur={(e) => {
                                    // Format to 4 decimal places when user leaves the field
                                    setSelectedCurrency(parseFloat(e.target.value || 0.0000).toFixed(4))
                                }}
                                className='bg-gradient-to-br from-theme-primary to-theme-secondary rounded-lg text-xl text-white p-3 w-40 text-center'
                                step="0.0001"
                            />
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-2">
                    <div className="flex items-center">
                        <div className="p-2">
                            <Label htmlFor="rate" value='Local Balance' />
                        </div>
                        <div className='p-2 w-1'>
                            <div id="rate" className='bg-gradient-to-br from-theme-primary to-theme-secondary rounded-lg text-xl text-white p-3 w-52 text-center'>
                                {parseFloat(localCurrencyAmount).toLocaleString('en-US', {
                                    minimumFractionDigits: 1,
                                    maximumFractionDigits: 1
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="col-span-1 flex items-center">
                        <div className="p-2">
                            <Label htmlFor="rate" value='Selected Balance' />
                        </div>
                        <div className='p-2 w-1'>
                            <div id="rate" className='bg-gradient-to-br from-theme-primary to-theme-secondary rounded-lg text-xl text-white p-3 w-52 text-center'>
                                {parseFloat(availableBalance).toLocaleString('en-US', {
                                    minimumFractionDigits: 1,
                                    maximumFractionDigits: 1
                                })}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-3">
                    <div className="col-span-2 flex items-center">
                        <div className="p-2 w-1/4">
                            <Label htmlFor="amount" value="Amount" />
                        </div>
                        <div className='w-1/2'>
                            <TextInput id="amount" onChange={(event) => { (event.target.value >= 0) && setCurrencyAmount(event.target.value) }} value={currencyAmount} type="number" sizing="lg" />
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-3">
                    <div className="col-span-3 flex">
                        <div className="p-2 w-1/5">
                            <Label htmlFor="commission" value="Commission Activity" />
                        </div>
                        <div className='w-4/5 pl-5 xl:pl-0'>
                            <div className="w-full xl:flex items-center">
                                <div className="xl:w-2/3 pb-3 xl:pb-0">
                                    <Radio id="per1" name="commission" value="per" className='bg-gray-400 mr-2' onChange={() => setCommissionType("per")} checked={commissionType === "per"} />
                                    <Label htmlFor="per1" className='mr-8'>Per %</Label>
                                    <Radio id="fixed1" name="commission" value="fixed" className='bg-gray-400 mr-2' onChange={() => setCommissionType("fixed")} checked={commissionType === "fixed"} />
                                    <Label htmlFor="fixed1" className='mr-10'>Fixed</Label>
                                </div>
                                <div className="xl:w-1/2">
                                    <Label htmlFor="value1" className='mr-5'>Value</Label>
                                    <input type="number" name="value1" id="value1" onChange={(event) => { event.target.value >= 0 && setCommissionVal(event.target.value) }} value={commissionVal} className='bg-gradient-to-br from-theme-primary to-theme-secondary rounded-lg text-md text-white' />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-3">
                    <div className="col-span-3 flex">
                        <div className="p-2 w-1/5">
                            <Label htmlFor="commission" value="Cash Handling" />
                        </div>
                        <div className='w-4/5'>
                            <div className="w-full flex items-center">
                                <Label htmlFor="value2" className='mr-5'>Value</Label>
                                <input type="number" name="cashHandling" id="cashHandling" value={cashHandling} onChange={(event) => { event.target.value >= 0 && setCashHandlingVal(event.target.value) }} className='bg-gradient-to-br from-theme-primary to-theme-secondary rounded-lg text-md text-white' />
                            </div>
                        </div>
                    </div>
                </div>
                <PaymentInfo showPayment={showPayment} setPaymentAmount={setPaymentAmount} setPaymentMethod={setPaymentMethod} />
                <div className="grid grid-cols-3">
                    <div className="col-span-3 flex items-center">
                        <div className="p-2 w-1/4">
                            <Label htmlFor="reverse" value="Reverse" />
                        </div>
                        <div className='w-1/2'>
                            <input id="reverse" className='bg-gradient-to-br from-theme-primary to-theme-secondary rounded-lg text-md text-white' defaultValue={0} onChange={(event) => { setReverse(event.target.value) }} type="number" />
                        </div>
                        <Button type='button' className='bg-gradient-to-br from-theme-primary to-theme-secondary text-white px-0' onClick={handleReverse}>Reverse</Button>
                    </div>
                </div>
                <div className="grid grid-cols-3">
                    <div className="col-span-2 flex items-center">
                        <div className="p-2 w-1/4">
                            <Label htmlFor="date" value="Date" />
                        </div>
                        <div className='w-1/2'>
                            <input className='w-[264px] rounded-lg border-0 text-md text-black cursor-pointer' onChange={(e) => setDate(createDateWithCurrentTime(e.target.value))} defaultValue={date} type="date" />
                        </div>

                    </div>
                </div>
                <div className='flex gap-4 mx-2 my-5'>
                    <Button type='button' className='bg-gradient-to-br from-theme-primary to-theme-secondary text-white px-5' onClick={handleTransactions}>Add Transaction</Button>
                    <Button type='button' onClick={() => handleSave(showPayment)} className='bg-gradient-to-br from-theme-primary to-theme-secondary text-white px-5'>Save and Settle</Button>
                </div>
            </form>
            <Modal dismissible size='md' show={openModal} onClose={() => setOpenModal(false)}>
                <Modal.Body className='bg-soft-gray border-dashed border-2 border-black'>
                    <div className="space-y-6">
                        <Invoice ref={componentRef} date={date} setDate={setDate} shopData={shopData} transactions={transactions} setTransactions={setTransactions} totalPayable={totalPayable} handleSaveAndPrint={handleSaveAndPrint} handleSubmit={handleSubmit} setOpenModal={setOpenModal} customerData={customerData} userDetail={userDetail} resetForm={resetForm} />

                        <ReactToPrint
                            trigger={() => (
                                <Button
                                    ref={printButtonRef}
                                    type='button'
                                    className='bg-red-600 text-white px-5 mx-auto hidden'
                                >
                                    Print
                                </Button>
                            )}
                            content={() => componentRef.current}
                            onBeforeGetContent={() => {
                                // Validate data before printing
                                if (!transactions || transactions.length === 0) {
                                    toast.error('No transactions to print');
                                    return false;
                                }
                                return Promise.resolve();
                            }}
                            onAfterPrint={() => {
                                // toast.success('Invoice printed successfully');
                                setOpenModal(false);
                            }}
                            pageStyle={`

                                @page {

                                    size: 480mm auto;

                                    margin: 5mm;

                                }

                                @media print {

                                    body {

                                        font-family: 'Courier New', Courier, monospace;

                                        font-size: 12px;

                                    }

                                    .invoice-to-print {
                                        width: 80mm;
                                    }

                                    .invoice-to-print > * + * {
                                        margin-top: 0 !important;
                                    }

                                    .invoice {

                                        display: block !important;

                                    }

                                    .save-btn, .cross-icon {
                                        display: none !important;
                                    }

                                    .print-bold {
                                        font-weight: bold;
                                    }

                                }

                                .invoice {

                                    display: none;

                                }

                                h1 {

                                    text-align: center;

                                    font-size: 24px;

                                    font-weight: bold;

                                }

                                table {

                                    width: 100%;

                                    border-collapse: collapse;

                                }

                                th, td {

                                    padding: 0;

                                    padding-bottom: 0 !important;

                                }

                                hr {

                                    border: none;

                                    border-top: 1px solid #000;

                                    margin: 5px 0;

                                }

                            `}

                        />
                        {/* <Button type='button' className='bg-red-600 text-white px-5 mx-auto' onClick={handleSubmit}>Print</Button> */}
                    </div>
                </Modal.Body>
            </Modal>

            {/* Modal for User Info */}
            <Modal dismissible size='md' show={userModal} onClose={() => setUserModal(false)}>
                <Modal.Body className='bg-soft-gray border-dashed border-2 border-black'>
                    <div className="space-y-6">
                        <ImCross className='ml-auto' onClick={() => { setUserModal(false); setOpenModal(true) }} />
                        <h2 className='text-center font-bold text-xl'>Save Customer Info</h2>
                        <form className="flex max-w-md flex-col gap-4">
                            <div>
                                <div className="mb-2 block">
                                    <Label htmlFor="customerName" value="Name" />
                                </div>
                                <TextInput id="customerName" name='customerName' placeholder='Customer Name' type="text" onChange={(e) => setCustomerData({ ...customerData, [e.target.name]: e.target.value })} required />
                            </div>
                            <div>
                                <div className="mb-2 block">
                                    <Label htmlFor="phoneNumber" value="Phone Number" />
                                </div>
                                <TextInput id="phoneNumber" name='phoneNumber' type="tel" placeholder="Customer Phone Number" onChange={(e) => setCustomerData({ ...customerData, [e.target.name]: e.target.value })} required />
                            </div>
                            <div>
                                <div className="mb-2 block">
                                    <Label htmlFor="email" value="Email" />
                                </div>
                                <TextInput id="email" name='customerEmail' type="email" placeholder="name@email.com" onChange={(e) => setCustomerData({ ...customerData, [e.target.name]: e.target.value })} />
                            </div>
                            <div>
                                <div className="mb-2 block">
                                    <Label htmlFor="idType" value="Id Type (Optional)" />
                                </div>
                                <TextInput id="idType" name='idType' type="text" placeholder="Id Type" onChange={(e) => setCustomerData({ ...customerData, [e.target.name]: e.target.value })} />
                            </div>
                            <div>
                                <div className="mb-2 block">
                                    <Label htmlFor="idIssuer" value="Id Issuer (Optional)" />
                                </div>
                                <TextInput id="idIssuer" name='idIssuer' type="text" placeholder="Id Issuer" onChange={(e) => setCustomerData({ ...customerData, [e.target.name]: e.target.value })} />
                            </div>
                            <div>
                                <div className="mb-2 block">
                                    <Label htmlFor="idNumber" value="Id Number (Optional)" />
                                </div>
                                <TextInput id="idNumber" name='idNumber' type="number" placeholder="Id Number" onChange={(e) => setCustomerData({ ...customerData, [e.target.name]: e.target.value })} />
                            </div>
                            <div>
                                <div className="mb-2 block">
                                    <Label htmlFor="idIssueDate" value="Id Issue Date (Optional)" />
                                </div>
                                <TextInput id="IdIssueDate" name='idIssueDate' type="date" placeholder="Id Issue Date" onChange={(e) => setCustomerData({ ...customerData, [e.target.name]: e.target.value })} />
                            </div>
                            <div>
                                <div className="mb-2 block">
                                    <Label htmlFor="idExpiryDate" value="Id Expiry Date (Optional)" />
                                </div>
                                <TextInput id="idExpiryDate" name='idExpiryDate' type="date" placeholder="id Expiry Date" onChange={(e) => setCustomerData({ ...customerData, [e.target.name]: e.target.value })} />
                            </div>

                            <div>
                                <div className="mb-2 block">
                                    <Label htmlFor="dob" value="Date of Birth (Optional)" />
                                </div>
                                <TextInput id="dob" name='dob' type="date" placeholder="Date of Birth" onChange={(e) => setCustomerData({ ...customerData, [e.target.name]: e.target.value })} />
                            </div>
                            <div>
                                <div className="mb-2 block">
                                    <Label htmlFor="address" value="Address (Optional)" />
                                </div>
                                <TextInput id="address" name='address' type="text" placeholder="Address" onChange={(e) => setCustomerData({ ...customerData, [e.target.name]: e.target.value })} />
                            </div>
                            <div>
                                <div className="mb-2 block">
                                    <Label htmlFor="occupation" value="Occupation (Optional)" />
                                </div>
                                <TextInput id="occupation" name='occupation' type="text" placeholder="Occupation" onChange={(e) => setCustomerData({ ...customerData, [e.target.name]: e.target.value })} />
                            </div>

                            <button type="submit" className='mt-5 py-2 text-white rounded-lg bg-gradient-to-br from-theme-primary to-theme-secondary' onClick={(e) => { handleCustomerInfo(e) }}>Save Info</button>
                        </form>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Success Modal for New Customer */}
            <Modal dismissible size="md" show={newCustomerModal} onClose={() => { setNewCustomerModal(false); setOpenModal(true); }} >
                {/* Modal Header */}
                <Modal.Header className="bg-green-50 border-b border-green-200">
                    <div className="flex items-center gap-3">
                        <svg
                            className="w-6 h-6 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <div>
                            <h2 className="text-lg font-semibold text-green-700">
                                New Customer Created!
                            </h2>
                            <p className="text-sm text-gray-600">
                                Customer information has been successfully saved.
                            </p>
                        </div>
                    </div>
                </Modal.Header>

                {/* Modal Body */}
                <Modal.Body className="bg-soft-gray px-6 py-4">
                    {/* Replace with your dynamic customer data */}
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-800">
                        <div className={`${existingCustomerData.name ? '' : 'hidden'}`}>
                            <span className="font-medium text-gray-600">Full Name:</span>
                            <p className="text-black">{existingCustomerData.name}</p>
                        </div>
                        <div className={`${existingCustomerData.phoneNumber ? '' : 'hidden'}`}>
                            <span className="font-medium text-gray-600">Phone:</span>
                            <p className="text-black">{existingCustomerData.phoneNumber}</p>
                        </div>
                        <div className={`${existingCustomerData.email ? '' : 'hidden'}`}>
                            <span className="font-medium text-gray-600">Email:</span>
                            <p className="text-black">{existingCustomerData.email}</p>
                        </div>
                        <div className={`${existingCustomerData.idType ? '' : 'hidden'}`}>
                            <span className="font-medium text-gray-600">Id Type:</span>
                            <p className="text-black">{existingCustomerData.idType}</p>
                        </div>
                        <div className={`${existingCustomerData.idIssuer ? '' : 'hidden'}`}>
                            <span className="font-medium text-gray-600">Id Issuer:</span>
                            <p className="text-black">{existingCustomerData.idIssuer}</p>
                        </div>
                        <div className={`${existingCustomerData.idNumber ? '' : 'hidden'}`}>
                            <span className="font-medium text-gray-600">Id Number:</span>
                            <p className="text-black">{existingCustomerData.idNumber}</p>
                        </div>
                        <div className={`${existingCustomerData.idIssueDate ? '' : 'hidden'}`}>
                            <span className="font-medium text-gray-600">Id Issue Date:</span>
                            <p className="text-black">{existingCustomerData.idIssueDate}</p>
                        </div>
                        <div className={`${existingCustomerData.idExpiryDate ? '' : 'hidden'}`}>
                            <span className="font-medium text-gray-600">Id Expiry Date:</span>
                            <p className="text-black">{existingCustomerData.idExpiryDate}</p>
                        </div>
                        <div className={`${existingCustomerData.dob ? '' : 'hidden'}`}>
                            <span className="font-medium text-gray-600">Date of Birth:</span>
                            <p className="text-black">{existingCustomerData.dob}</p>
                        </div>
                        <div className={`${existingCustomerData.address ? '' : 'hidden'}`}>
                            <span className="font-medium text-gray-600">Address:</span>
                            <p className="text-black">{existingCustomerData.address}</p>
                        </div>
                        <div className={`${existingCustomerData.occupation ? '' : 'hidden'}`}>
                            <span className="font-medium text-gray-600">Occupation:</span>
                            <p className="text-black">{existingCustomerData.occupation}</p>
                        </div>
                    </div>

                    {/* Button */}
                    <div className="mt-6 text-center">
                        <Button
                            onClick={() => {
                                setNewCustomerModal(false);
                                setOpenModal(true);
                            }}
                            className="bg-gradient-to-br from-theme-primary to-theme-secondary text-white px-6 py-2"
                        >
                            Continue
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Transaction History Modal for Customer */}
            <Modal dismissible size="7xl" show={showCustomerHistoryModal} onClose={() => { setShowCustomerHistoryModal(false); setOpenModal(true); }}>
                <Modal.Header>
                    <div className="flex flex-col">
                        <h3 className="text-xl font-bold">Customer Information</h3>
                        <p className="text-sm text-gray-500">Regular Customer</p>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {/* Customer Info Section */}
                    <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-lg font-semibold mb-4">Customer Details</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div className={`${existingCustomerData.name ? '' : 'hidden'}`}>
                                <p className="text-sm text-gray-600">Name</p>
                                <p className="font-medium">{existingCustomerData.name}</p>
                            </div>
                            <div className={`${existingCustomerData.phoneNumber ? '' : 'hidden'}`}>
                                <p className="text-sm text-gray-600">Phone</p>
                                <p className="font-medium">{existingCustomerData.phoneNumber}</p>
                            </div>
                            <div className={`${existingCustomerData.email ? '' : 'hidden'}`}>
                                <p className="text-sm text-gray-600">Email</p>
                                <p className="font-medium">{existingCustomerData.email}</p>
                            </div>
                            <div className={`${existingCustomerData.idType ? '' : 'hidden'}`}>
                                <p className="text-sm text-gray-600">Id Type</p>
                                <p className="font-medium">{existingCustomerData.idType}</p>
                            </div>
                            <div className={`${existingCustomerData.idIssuer ? '' : 'hidden'}`}>
                                <p className="text-sm text-gray-600">Id Issuer</p>
                                <p className="font-medium">{existingCustomerData.idIssuer}</p>
                            </div>
                            <div className={`${existingCustomerData.idNumber ? '' : 'hidden'}`}>
                                <p className="text-sm text-gray-600">Id Number</p>
                                <p className="font-medium">{existingCustomerData.idNumber}</p>
                            </div>
                            <div className={`${existingCustomerData.idIssueDate ? '' : 'hidden'}`}>
                                <p className="text-sm text-gray-600">Id Issue Date</p>
                                <p className="font-medium">{existingCustomerData.idIssueDate}</p>
                            </div>
                            <div className={`${existingCustomerData.idExpiryDate ? '' : 'hidden'}`}>
                                <p className="text-sm text-gray-600">Id Expiry Date</p>
                                <p className="font-medium">{existingCustomerData.idExpiryDate}</p>
                            </div>
                            <div className={`${existingCustomerData.dob ? '' : 'hidden'}`}>
                                <p className="text-sm text-gray-600">Date of Birth</p>
                                <p className="font-medium">{existingCustomerData.dob}</p>
                            </div>
                            <div className={`${existingCustomerData.address ? '' : 'hidden'}`}>
                                <p className="text-sm text-gray-600">Address</p>
                                <p className="font-medium">{existingCustomerData.address}</p>
                            </div>
                            <div className={`${existingCustomerData.occupation ? '' : 'hidden'}`}>
                                <p className="text-sm text-gray-600">Occupation</p>
                                <p className="font-medium">{existingCustomerData.occupation}</p>
                            </div>

                        </div>
                    </div>

                    {/* Transaction History Section */}
                    <div>
                        <h4 className="text-lg font-semibold mb-4">Transaction History</h4>
                        <div className="overflow-x-auto">
                            <Table hoverable>
                                <Table.Head>
                                    <Table.HeadCell>Date</Table.HeadCell>
                                    <Table.HeadCell>Transaction ID</Table.HeadCell>
                                    <Table.HeadCell>Currency</Table.HeadCell>
                                    <Table.HeadCell>Amount</Table.HeadCell>
                                    <Table.HeadCell>Rate</Table.HeadCell>
                                    <Table.HeadCell>Commission</Table.HeadCell>
                                    <Table.HeadCell>Total</Table.HeadCell>
                                    <Table.HeadCell>Status</Table.HeadCell>
                                </Table.Head>
                                <Table.Body className="divide-y">
                                    {existingCustomerTransactions.map((transaction, index) => (
                                        <Table.Row key={index} className="bg-white">
                                            <Table.Cell>{formatDate(transaction.date)}</Table.Cell>
                                            <Table.Cell>{transaction.transactionId}</Table.Cell>
                                            <Table.Cell>{transaction.currency}</Table.Cell>
                                            <Table.Cell>{formatCurrency(transaction.amount, transaction.currency)}</Table.Cell>
                                            <Table.Cell>{parseFloat(transaction.exchangeValue).toFixed(4)}</Table.Cell>
                                            <Table.Cell>{formatCurrency(transaction.commissionAmount, 'AUD')}</Table.Cell>
                                            <Table.Cell>{formatCurrency(transaction.currencyAmount, 'AUD')}</Table.Cell>
                                            <Table.Cell>
                                                <Badge color={transaction.status === 'completed' ? 'success' : 'warning'}>
                                                    {transaction.status}
                                                </Badge>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    )
}

export default FormTemplate;